/**
 * CustomerForecastDetail — Detail-Ansicht eines Kunden-Forecasts fuer CS-Manager.
 *
 * Features:
 * - Kunden-Info Header mit Status
 * - Two-Column Layout: Tageswerte links, Plausibility + Actions rechts
 * - Tageswerte-Tabelle mit SOLL/IST und editierbaren Werten
 * - Plausibility-Card mit 6 Checks
 * - Approve/Reject Actions mit Kommentar
 * - Zurueck-Button
 *
 * @page /kunden-forecasts/:id
 * @roles admin, manager
 * @api /api/customer-forecasts/submissions/:id, approve, reject, values
 * @i18n customerForecast
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, AlertTriangle, Edit3, Save, Zap } from 'lucide-react';
import { Card, PageHeader, Button } from '../components/ui';
import BackLink from '../components/BackLink';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';

function api(path, opts) {
  return fetch(`${API}${path}`, { credentials: 'include', ...opts })
    .then(async r => {
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      return data;
    });
}

function fmt(n) {
  if (n == null) return '\u2013';
  return Number(n).toLocaleString('de-DE');
}

function SeverityIcon({ severity }) {
  if (severity === 'ok') return <CheckCircle className="w-4 h-4 text-emerald-500" />;
  if (severity === 'warning') return <AlertTriangle className="w-4 h-4 text-amber-500" />;
  return <XCircle className="w-4 h-4 text-red-500" />;
}

function StatusBadge({ status, t }) {
  const cfg = {
    submitted: { bg: 'bg-blue-500/10', text: 'text-blue-600', label: t('customerForecast.statusSubmitted') },
    approved: { bg: 'bg-emerald-500/10', text: 'text-emerald-600', label: t('customerForecast.statusApproved') },
    rejected: { bg: 'bg-red-500/10', text: 'text-red-600', label: t('customerForecast.statusRejected') },
    draft: { bg: 'bg-gray-500/10', text: 'text-[var(--text-secondary)]', label: t('customerForecast.statusDraft') },
    in_review: { bg: 'bg-amber-500/10', text: 'text-amber-600', label: t('customerForecast.statusInReview') },
  };
  const c = cfg[status] || cfg.draft;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[12px] font-semibold ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

export default function CustomerForecastDetail() {
  const { t } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  // #1633 (#1629 P6): zurück zum Herkunftskontext (Cockpit/Review/…), Fallback Review-Liste
  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/forecast-review');
  };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api(`/api/customer-forecasts/submissions/${id}`);
      setData(result);
      setEditValues(result.forecast?.daily_values || {});
    } catch (err) {
      console.error('Load error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleApprove = async () => {
    setApproving(true);
    setError('');
    try {
      await api(`/api/customer-forecasts/submissions/${id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment: comment || null }),
      });
      await loadData();
      setComment('');
    } catch (err) {
      setError(err.message);
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!comment.trim()) {
      setError(t('customerForecast.commentRequired'));
      return;
    }
    setRejecting(true);
    setError('');
    try {
      await api(`/api/customer-forecasts/submissions/${id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment }),
      });
      await loadData();
      setComment('');
    } catch (err) {
      setError(err.message);
    } finally {
      setRejecting(false);
    }
  };

  const handleSaveValues = async () => {
    setSaving(true);
    setError('');
    try {
      await api(`/api/customer-forecasts/submissions/${id}/values`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ daily_values: editValues }),
      });
      setEditing(false);
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data || !data.forecast) {
    return (
      <div className="p-6">
        <BackLink onClick={handleBack} label={t('customerForecast.backToOverview')} className="mb-4" />
        <Card><div className="text-center py-12 text-[var(--text-tertiary)]">{t('customerForecast.notFound')}</div></Card>
      </div>
    );
  }

  const { forecast, plausibility, actuals, operative_soll, aktionen } = data;
  const dv = forecast.daily_values || {};
  const opSoll = operative_soll || {};
  const daysInMonth = new Date(forecast.year, forecast.month, 0).getDate();
  const canReview = ['submitted', 'in_review'].includes(forecast.status);
  const hasOperative = Object.keys(opSoll).length > 0;

  // Build actuals map from array
  const actualsMap = {};
  if (actuals) {
    for (const a of actuals) {
      actualsMap[a.day] = a.actual_parcels;
    }
  }

  // Check if month is in the future (no IST data expected)
  const isFutureMonth = new Date(forecast.year, forecast.month - 1, 1) > new Date();

  // Build AZ day map
  const azDayMap = {};
  if (aktionen) {
    for (const az of aktionen) {
      const von = az.datum_von?.slice?.(0, 10) || (az.datum_von instanceof Date ? az.datum_von.toISOString().slice(0, 10) : '');
      const bis = az.datum_bis?.slice?.(0, 10) || (az.datum_bis instanceof Date ? az.datum_bis.toISOString().slice(0, 10) : '');
      for (let d = 1; d <= daysInMonth; d++) {
        const ds = `${forecast.year}-${String(forecast.month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        if (ds >= von && ds <= bis) {
          azDayMap[d] = az;
        }
      }
    }
  }

  // Compute totals — use operative values when available
  const getEffectiveSoll = (day) => {
    if (editing) return parseInt(editValues[String(day)]) || 0;
    if (hasOperative && opSoll[day] != null) return opSoll[day];
    return parseInt(dv[String(day)]) || 0;
  };
  const editTotal = Array.from({ length: daysInMonth }, (_, i) => getEffectiveSoll(i + 1)).reduce((s, v) => s + v, 0);
  const customerTotal = Object.values(dv).reduce((s, v) => s + (parseInt(v) || 0), 0);
  const actualTotal = Object.values(actualsMap).reduce((s, v) => s + (parseInt(v) || 0), 0);

  // Format deadline
  const fmtDeadline = (dl) => {
    if (!dl) return '\u2013';
    try { return new Date(dl).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return dl; }
  };

  return (
    <div className="p-6">
      {/* Back Button */}
      <BackLink onClick={handleBack} label={t('customerForecast.backToOverview')} className="mb-4" />

      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">{forecast.kunden_name}</h1>
          <p className="text-sm text-[var(--text-secondary)]">
            {t(`common.months.${forecast.month - 1}`)} {forecast.year} &middot; {t('customerForecast.deadline')}: {fmtDeadline(forecast.deadline)}
          </p>
        </div>
        <StatusBadge status={forecast.status} t={t} />
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/10 text-red-600 text-sm border border-red-500/20">
          {error}
        </div>
      )}

      {/* Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Daily Values Table */}
        <div className="lg:col-span-2">
          <Card>
            <div className="flex items-center justify-between mb-4 px-4 pt-4">
              <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('customerForecast.dailyValues')}</h2>
              {canReview && !editing && (
                <button
                  onClick={() => { setEditing(true); setEditValues({ ...dv }); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-[var(--border-card)] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors"
                >
                  <Edit3 className="w-3.5 h-3.5" /> {t('customerForecast.editValues')}
                </button>
              )}
              {editing && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditing(false)}
                    className="px-3 py-1.5 rounded-lg text-sm border border-[var(--border-card)] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleSaveValues}
                    disabled={saving}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors disabled:opacity-50"
                  >
                    <Save className="w-3.5 h-3.5" /> {saving ? '...' : t('common.save')}
                  </button>
                </div>
              )}
            </div>
            <div className="overflow-x-auto px-4 pb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--border-card)]">
                    <th className="text-left py-2 px-3 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] w-16">{t('customerForecast.colDay')}</th>
                    <th className="text-left py-2 px-3 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] w-16">{t('customerForecast.colWeekday')}</th>
                    <th className="text-right py-2 px-3 text-[11px] font-semibold uppercase text-[var(--text-tertiary)]">{t('customerForecast.colSoll')}</th>
                    {hasOperative && <th className="text-right py-2 px-3 text-[11px] font-semibold uppercase text-[var(--text-tertiary)]">{t('customerForecast.colOperativ')}</th>}
                    <th className="text-right py-2 px-3 text-[11px] font-semibold uppercase text-[var(--text-tertiary)]">{t('customerForecast.colIst')}</th>
                    <th className="text-right py-2 px-3 text-[11px] font-semibold uppercase text-[var(--text-tertiary)]">{t('customerForecast.colDiff')}</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                    const date = new Date(forecast.year, forecast.month - 1, day);
                    const dayOfWeek = date.getDay(); // 0=Sun
                    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                    const weekdayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                    const weekdayKey = `common.weekdays.${dayOfWeek}`;
                    const dayAz = azDayMap[day];

                    const kundenSoll = parseInt(dv[String(day)]) || 0;
                    const operativSoll = opSoll[day] != null ? opSoll[day] : null;
                    const soll = editing ? (parseInt(editValues[String(day)]) || 0) : kundenSoll;
                    const effectiveSoll = hasOperative && operativSoll != null ? operativSoll : soll;
                    const ist = parseInt(actualsMap[day]) || 0;
                    // Show diff only when IST data exists (not for future months)
                    const diff = (effectiveSoll > 0 && ist > 0) ? ((ist - effectiveSoll) / effectiveSoll * 100) : null;

                    const typColors = { black_week: '#EF4444', sale: '#F59E0B', launch: '#3B82F6', kampagne: '#8B5CF6', saison: '#06B6D4', sonstige: '#6B7280' };

                    return (
                      <tr
                        key={day}
                        className={`border-b border-[var(--border-card)] ${isWeekend ? 'bg-[var(--bg-hover)]' : ''}`}
                        style={dayAz && !isWeekend ? { backgroundColor: (typColors[dayAz.typ] || '#F59E0B') + '10' } : undefined}
                      >
                        <td className="py-2 px-3 text-[var(--text-primary)] font-mono">
                          <span className="flex items-center gap-1.5">
                            {day}
                            {dayAz && (
                              <span
                                className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1 py-0.5 rounded"
                                style={{ backgroundColor: (typColors[dayAz.typ] || '#F59E0B') + '20', color: typColors[dayAz.typ] || '#F59E0B' }}
                                title={`${dayAz.titel} (${dayAz.multiplikator ? dayAz.multiplikator + 'x' : '+' + dayAz.zusatz_orders})`}
                              >
                                <Zap className="w-3 h-3" />{dayAz.multiplikator ? dayAz.multiplikator + 'x' : '+' + fmt(dayAz.zusatz_orders)}
                              </span>
                            )}
                          </span>
                        </td>
                        <td className={`py-2 px-3 text-[12px] ${isWeekend ? 'text-[var(--text-tertiary)]' : 'text-[var(--text-secondary)]'}`}>
                          {t(weekdayKey) || weekdayNames[dayOfWeek]}
                        </td>
                        <td className="py-2 px-3 text-right">
                          {editing ? (
                            <input
                              type="number"
                              value={editValues[String(day)] || ''}
                              onChange={e => setEditValues(prev => ({ ...prev, [String(day)]: e.target.value }))}
                              className="w-20 text-right px-2 py-1 rounded border border-[var(--border-card)] bg-[var(--bg-surface)] text-[var(--text-primary)] text-sm"
                              min="0"
                            />
                          ) : (
                            <span className="font-mono text-[var(--text-primary)]">{soll > 0 ? fmt(soll) : '\u2013'}</span>
                          )}
                        </td>
                        {hasOperative && (
                          <td className="py-2 px-3 text-right font-mono">
                            {operativSoll != null && operativSoll !== kundenSoll ? (
                              <span className="text-[#338edc] font-semibold">{fmt(operativSoll)}</span>
                            ) : (
                              <span className="text-[var(--text-tertiary)]">{operativSoll != null ? fmt(operativSoll) : '\u2013'}</span>
                            )}
                          </td>
                        )}
                        <td className="py-2 px-3 text-right font-mono text-[var(--text-secondary)]">
                          {ist > 0 ? fmt(ist) : '\u2013'}
                        </td>
                        <td className="py-2 px-3 text-right">
                          {diff != null ? (
                            <span className={`text-[11px] font-semibold ${
                              Math.abs(diff) <= 5 ? 'text-[var(--text-secondary)]'
                              : diff > 0 ? 'text-emerald-500'
                              : 'text-red-500'
                            }`}>
                              {diff > 0 ? '+' : ''}{diff.toFixed(0)}%
                            </span>
                          ) : '\u2013'}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Totals Row */}
                  <tr className="font-semibold bg-[var(--bg-hover)]">
                    <td className="py-2.5 px-3 text-[var(--text-primary)]" colSpan={2}>{t('customerForecast.total')}</td>
                    <td className="py-2.5 px-3 text-right font-mono text-[var(--text-primary)]">{fmt(customerTotal)}</td>
                    {hasOperative && (
                      <td className="py-2.5 px-3 text-right font-mono text-[#338edc]">
                        {editTotal !== customerTotal ? fmt(editTotal) : fmt(customerTotal)}
                      </td>
                    )}
                    <td className="py-2.5 px-3 text-right font-mono text-[var(--text-secondary)]">{actualTotal > 0 ? fmt(actualTotal) : '\u2013'}</td>
                    <td className="py-2.5 px-3 text-right">
                      {editTotal > 0 && actualTotal > 0 ? (
                        <span className={`text-[12px] font-semibold ${
                          Math.abs((actualTotal - editTotal) / editTotal * 100) <= 5 ? 'text-[var(--text-secondary)]'
                          : actualTotal > editTotal ? 'text-emerald-500'
                          : 'text-red-500'
                        }`}>
                          {((actualTotal - editTotal) / editTotal * 100) > 0 ? '+' : ''}
                          {((actualTotal - editTotal) / editTotal * 100).toFixed(1)}%
                        </span>
                      ) : '\u2013'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Right: Plausibility + Actions */}
        <div className="space-y-6">
          {/* Plausibility Card */}
          <Card>
            <div className="p-4">
              <h3 className="text-base font-semibold text-[var(--text-primary)] mb-4">{t('customerForecast.plausibilityTitle')}</h3>
              {plausibility?.checks?.length > 0 ? (
                <div className="space-y-3">
                  {plausibility.checks.map((check, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <SeverityIcon severity={check.severity} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium text-[var(--text-primary)]">{check.label}</div>
                        <div className="text-[12px] text-[var(--text-secondary)]">{check.value}</div>
                        {check.reference && (
                          <div className="text-[11px] text-[var(--text-tertiary)]">
                            {t('customerForecast.reference')}: {fmt(check.reference)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[var(--text-tertiary)]">{t('customerForecast.noPlausibilityData')}</p>
              )}
              {plausibility?.overall && (
                <div className={`mt-4 pt-3 border-t border-[var(--border-card)] flex items-center gap-2`}>
                  <SeverityIcon severity={plausibility.overall} />
                  <span className="text-sm font-medium text-[var(--text-primary)]">
                    {plausibility.overall === 'ok' ? t('customerForecast.plausibilityOk')
                      : plausibility.overall === 'warning' ? t('customerForecast.plausibilityWarning')
                      : t('customerForecast.plausibilityError')}
                  </span>
                </div>
              )}
            </div>
          </Card>

          {/* Info Card */}
          <Card>
            <div className="p-4 space-y-3">
              <h3 className="text-base font-semibold text-[var(--text-primary)] mb-3">{t('customerForecast.details')}</h3>
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <span className="text-[var(--text-tertiary)]">{t('customerForecast.totalForecast')}</span>
                <span className="text-right font-mono text-[var(--text-primary)]">{fmt(forecast.total_forecast)}</span>
                <span className="text-[var(--text-tertiary)]">{t('customerForecast.deadline')}</span>
                <span className="text-right text-[var(--text-primary)]">{fmtDeadline(forecast.deadline)}</span>
                {forecast.submitted_at && (
                  <>
                    <span className="text-[var(--text-tertiary)]">{t('customerForecast.submittedAt')}</span>
                    <span className="text-right text-[var(--text-primary)]">{new Date(forecast.submitted_at).toLocaleDateString('de-DE')}</span>
                  </>
                )}
                {forecast.reviewed_at && (
                  <>
                    <span className="text-[var(--text-tertiary)]">{t('customerForecast.reviewedAt')}</span>
                    <span className="text-right text-[var(--text-primary)]">{new Date(forecast.reviewed_at).toLocaleDateString('de-DE')}</span>
                  </>
                )}
                {forecast.review_comment && (
                  <>
                    <span className="text-[var(--text-tertiary)]">{t('customerForecast.reviewComment')}</span>
                    <span className="text-right text-[var(--text-primary)] text-[12px]">{forecast.review_comment}</span>
                  </>
                )}
                <span className="text-[var(--text-tertiary)]">{t('customerForecast.synced')}</span>
                <span className="text-right text-[var(--text-primary)]">{forecast.synced_to_forecast ? '\u2713' : '\u2013'}</span>
              </div>
            </div>
          </Card>

          {/* Actions Card */}
          {canReview && (
            <Card>
              <div className="p-4">
                <h3 className="text-base font-semibold text-[var(--text-primary)] mb-3">{t('customerForecast.actions')}</h3>
                <textarea
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  placeholder={t('customerForecast.commentPlaceholder')}
                  className="w-full px-3 py-2 rounded-lg border border-[var(--border-card)] bg-[var(--bg-surface)] text-[var(--text-primary)] text-sm resize-none"
                  rows={3}
                />
                <div className="flex items-center gap-3 mt-3">
                  <button
                    onClick={handleApprove}
                    disabled={approving}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
                  >
                    <CheckCircle className="w-4 h-4" />
                    {approving ? '...' : t('customerForecast.approve')}
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={rejecting}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    <XCircle className="w-4 h-4" />
                    {rejecting ? '...' : t('customerForecast.reject')}
                  </button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
